import { useTranslation } from 'react-i18next';
import {
  Button,
  Icon,
  Flex,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';

/**
 * Which flow the banner is shown in. Each variant gets its own copy so the
 * prompt matches what the assistant will actually do:
 * - `connect`   → Add Connection (credentials/permissions/setup)
 * - `diagnosis` → Edit Connection (health check / troubleshooting)
 * - `discover`  → Configure Actions (choosing what to enable)
 * - `trigger`   → Poll/Webhook Trigger setup (choosing/obtaining field values)
 */
type TIntegrationAssistantBannerVariant =
  | 'connect'
  | 'diagnosis'
  | 'discover'
  | 'trigger';

interface IIntegrationAssistantBannerProps {
  onOpenAssistant: () => void;
  variant?: TIntegrationAssistantBannerVariant;
  compact?: boolean;
}

const BANNER_COPY_KEYS: Record<
  TIntegrationAssistantBannerVariant,
  { title: string; description: string }
> = {
  connect: {
    title: 'integrations.assistant.banner.title',
    description: 'integrations.assistant.banner.description',
  },
  diagnosis: {
    title: 'integrations.assistant.banner.diagnosis.title',
    description: 'integrations.assistant.banner.diagnosis.description',
  },
  discover: {
    title: 'integrations.assistant.banner.discover.title',
    description: 'integrations.assistant.banner.discover.description',
  },
  trigger: {
    title: 'integrations.assistant.banner.trigger.title',
    description: 'integrations.assistant.banner.trigger.description',
  },
};

/**
 * Banner shown when the integration assistant panel is collapsed. Prompts the
 * user to open the AI assistant; the copy is tailored to the flow via
 * `variant` (connect / diagnosis / discover).
 */
export function IntegrationAssistantBanner({
  onOpenAssistant,
  variant = 'connect',
  compact = false,
}: IIntegrationAssistantBannerProps) {
  const { t } = useTranslation('integrations');
  const copyKeys = BANNER_COPY_KEYS[variant];

  if (compact) {
    const label = t('integrations.assistant.banner.compactButton');
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon-sm"
            onClick={onOpenAssistant}
            aria-label={label}
            className="shrink-0"
            data-testid="integration-assistant-open-button"
          >
            <Icon type="Sparkle" size="sm" className="text-primary" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    );
  }

  const openButton = (
    <Button
      variant="secondary"
      size="sm"
      onClick={onOpenAssistant}
      className="shrink-0"
      data-testid="integration-assistant-open-button"
    >
      <Icon type="Sparkle" size="sm" className="text-primary" />
      {t('integrations.assistant.banner.button')}
    </Button>
  );

  return (
    <Flex
      align="center"
      justify="between"
      gap={3}
      className="rounded-lg border border-border px-4 py-3"
      data-testid="integration-assistant-banner"
    >
      <Flex direction="column" gap={1} className="flex-1 min-w-0">
        <Text
          weight="medium"
          className="truncate text-sm [@media(min-height:721px)]:text-base"
        >
          {t(copyKeys.title)}
        </Text>
        <Text
          color="muted"
          level="small"
          className="[@media(max-height:720px)]:hidden"
        >
          {t(copyKeys.description)}
        </Text>
      </Flex>
      {openButton}
    </Flex>
  );
}
